import React, { FC, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import classNames from 'classnames'
import { TransactionAlertSettingsStyles } from './TransactionAlertSettings.styles'

// The drawer (or the nudge dialog card) the sheet should sit in: an explicit marker first,
// else the nearest fixed-position ancestor (the profile drawer's panel).
export const findSheetAnchor = (el: HTMLElement | null): HTMLElement | null => {
  if (!el) {
    return null
  }
  const marked = el.closest('[data-sheet-anchor]') as HTMLElement | null
  if (marked) {
    return marked
  }
  let node: HTMLElement | null = el.parentElement
  while (node && node !== document.body) {
    if (window.getComputedStyle(node).position === 'fixed') {
      return node
    }
    node = node.parentElement
  }
  return null
}

interface AnchoredSheetProps {
  // Element to cover; null falls back to a page-centred dialog
  anchor: HTMLElement | null
  ariaLabel?: string
  // Extra class on the sheet card (e.g. a compact padding variant)
  sheetClassName?: string
  onDismiss: () => void
  children: React.ReactNode
}

// iOS-style bottom sheet confined to its anchor: the scrim covers only the drawer / card and
// the sheet slides up from its bottom edge, so the control just tapped stays in view above it.
export const AnchoredSheet: FC<AnchoredSheetProps> = ({
  anchor,
  ariaLabel,
  sheetClassName,
  onDismiss,
  children
}) => {
  const classes = TransactionAlertSettingsStyles()
  const [rect, setRect] = useState<DOMRect | null>(() =>
    anchor ? anchor.getBoundingClientRect() : null
  )

  useEffect(() => {
    if (!anchor) {
      setRect(null)
      return undefined
    }
    // Follow the anchor while the sheet is open (the drawer slides / the window resizes):
    // one rAF loop, updating state only when the box actually moved.
    let frame = 0
    let last = ''
    const tick = () => {
      const next = anchor.getBoundingClientRect()
      const key = `${next.left},${next.top},${next.width},${next.height}`
      if (key !== last) {
        last = key
        setRect(next)
      }
      frame = window.requestAnimationFrame(tick)
    }
    tick()
    return () => window.cancelAnimationFrame(frame)
  }, [anchor])

  const anchored = Boolean(rect)
  // Inside a rounded dialog card the sheet's bottom corners follow the card's radius; in the
  // drawer (flush with the screen edge) they stay square.
  const roundedBottom = anchored && Boolean(anchor && anchor.hasAttribute('data-sheet-anchor'))
  const scrimStyle = rect
    ? { top: rect.top, left: rect.left, width: rect.width, height: rect.height }
    : undefined

  return createPortal(
    <div
      className={classNames(classes.sheetScrim, {
        [classes.anchoredScrim]: anchored,
        [classes.anchoredScrimRounded]: roundedBottom
      })}
      style={scrimStyle}
      onClick={onDismiss}
    >
      <div
        className={classNames(
          classes.sheet,
          {
            [classes.bottomSheet]: anchored,
            [classes.bottomSheetRounded]: roundedBottom
          },
          sheetClassName
        )}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>,
    document.body
  )
}

export default AnchoredSheet
